import { z } from "zod";

/**
 * Wire-shape mirror of the shore's parallel run COMPARISON DTOs (PAYD-FR-101
 * to FR-103, `parallel-run.dto.ts` and `comparison.ts`).
 *
 * The difference in `ComparisonTotals` is always COMPUTED MINUS LEGACY: a
 * positive number means Pulsewise pays more than the register being
 * replaced. One direction, fixed by the shore, so this screen never has to
 * re-derive the sign.
 */

const DECIMAL = /^-?\d{1,15}(\.\d{1,6})?$/;

export const VARIANCE_TIERS = ["exact", "within_tolerance", "outside"] as const;
export type VarianceTier = (typeof VARIANCE_TIERS)[number];

export const VARIANCE_CLASSIFICATIONS = [
  "legacy_wrong",
  "method_difference",
  "data_migration",
  "defect",
  "unclassified",
] as const;
export type VarianceClassification = (typeof VARIANCE_CLASSIFICATIONS)[number];

export const VARIANCE_SCOPES = ["member_net", "component"] as const;
export type VarianceScope = (typeof VARIANCE_SCOPES)[number];

export const sideTotalsSchema = z
  .object({
    gross: z.string(),
    deduction: z.string(),
    net: z.string(),
    employerContribution: z.string(),
    statutory: z.string(),
    memberCount: z.number().int(),
  })
  .strict();
export type SideTotals = z.infer<typeof sideTotalsSchema>;

export const comparisonTotalsSchema = z
  .object({
    legacy: sideTotalsSchema,
    computed: sideTotalsSchema,
    difference: sideTotalsSchema,
  })
  .strict();
export type ComparisonTotals = z.infer<typeof comparisonTotalsSchema>;

export const toleranceSchema = z.object({ absolute: z.string(), percent: z.string() }).strict();

export const exitCriterionResultSchema = z
  .object({ code: z.string(), met: z.boolean(), expected: z.string(), actual: z.string() })
  .strict();
export type ExitCriterionResult = z.infer<typeof exitCriterionResultSchema>;

export const parallelRunComparisonSchema = z
  .object({
    id: z.string(),
    importId: z.string(),
    payrollRunId: z.string().nullable(),
    cycleStart: z.string(),
    currency: z.string(),
    tolerances: toleranceSchema,
    totals: comparisonTotalsSchema,
    memberMatchPercent: z.string(),
    memberCount: z.number().int(),
    exactCount: z.number().int(),
    withinToleranceCount: z.number().int(),
    outsideCount: z.number().int(),
    unmatchedCount: z.number().int(),
    computedSlipIds: z.array(z.string()),
    reconciliationSnapshotId: z.string().nullable(),
    /// `Record<string, string>`, not a typed union: the exit criteria a
    /// workspace agreed on are configuration, and this screen only ever
    /// echoes them back beside `exitCriteriaResults`.
    exitCriteria: z.record(z.string(), z.string()),
    exitCriteriaResults: z.array(exitCriterionResultSchema),
    passed: z.boolean(),
    status: z.string(),
    comparedAt: z.string(),
    version: z.number().int(),
  })
  .strict();
export type ParallelRunComparison = z.infer<typeof parallelRunComparisonSchema>;

export const parallelRunVarianceSchema = z
  .object({
    id: z.string(),
    membershipId: z.string().nullable(),
    identifier: z.string(),
    componentId: z.string().nullable(),
    componentCode: z.string().nullable(),
    legacyAmount: z.string().nullable(),
    computedAmount: z.string().nullable(),
    difference: z.string(),
    currency: z.string(),
    scope: z.enum(VARIANCE_SCOPES),
    tier: z.enum(VARIANCE_TIERS),
    classification: z.enum(VARIANCE_CLASSIFICATIONS),
    note: z.string().nullable(),
    version: z.number().int(),
  })
  .strict();
export type ParallelRunVariance = z.infer<typeof parallelRunVarianceSchema>;

export const parallelRunVarianceListSchema = z
  .object({ rows: z.array(parallelRunVarianceSchema), nextCursor: z.string().nullable() })
  .strict();
export type ParallelRunVarianceList = z.infer<typeof parallelRunVarianceListSchema>;

export const parallelRunVarianceNotesSchema = z.object({ notes: z.array(z.string()) }).strict();
export type ParallelRunVarianceNotes = z.infer<typeof parallelRunVarianceNotesSchema>;

// -- Requests -----------------------------------------------------------------

export const compareParallelRunSchema = z.object({
  toleranceAbsolute: z.string().regex(DECIMAL).optional(),
  tolerancePercent: z.string().regex(DECIMAL).optional(),
  netMatchPercentMin: z.string().regex(DECIMAL).optional(),
  statutoryToleranceAbsolute: z.string().regex(DECIMAL).optional(),
});
export type CompareParallelRunInput = z.infer<typeof compareParallelRunSchema>;

/// Request mirror of `ClassifyVarianceDto`. `note` stays optional here the
/// same way the shore's DTO does; `noteRequired` in
/// `parallel-run-classification.ts` is where "mandatory on anything but
/// unclassified" (FR-102) actually lives, tested on its own.
export const classifyVarianceSchema = z.object({
  version: z.number().int(),
  classification: z.enum(VARIANCE_CLASSIFICATIONS),
  note: z.string().min(3).max(1000).optional(),
});
export type ClassifyVarianceInput = z.infer<typeof classifyVarianceSchema>;
